import { parseFontFamilyName } from "@hightouch/lib/ad-studio/html-editor/font-family";
import type { TextLayer } from "@hightouch/lib/ad-studio/html-editor/model";
import type { LayerPlan } from "src/pages/ad-studio/layered-export/plan-layers";
import type {
  LayeredExportArgs,
  PreparedLayeredExport,
} from "src/pages/ad-studio/layered-export/prepare-layered-export";
import { parseCssColor } from "@hightouch/lib/ad-studio/psd-export/psd-color";
import { buildCanvasFontMetricsResolver } from "@hightouch/lib/ad-studio/psd-export/psd-font-metrics";
import {
  firstBaselineOffset,
  leadingFromLineHeight,
  textLayerTransform,
  textSegmentsOf,
  trackingFromLetterSpacing,
} from "@hightouch/lib/ad-studio/psd-export/text-layer-data";

type EditableText = NonNullable<
  Extract<LayerPlan, { kind: "leaf" }>["editableText"]
>;

export function createIdmlTextPreparer({
  doc,
  measurements,
  resolveFont,
}: Pick<PreparedLayeredExport, "doc" | "measurements"> &
  Pick<LayeredExportArgs, "resolveFont">) {
  const measureFont = buildCanvasFontMetricsResolver();
  return (source: EditableText) => {
    const { layer, parentTransform, ancestors } = source;
    const whitespace = inheritedWhitespace(source);
    const segments = idmlTextSegments(layer, whitespace.collapse);
    if (
      !supportsNativeText(layer, segments) ||
      whitespace.wrap !== "wrap" ||
      !["collapse", "preserve-breaks"].includes(whitespace.collapse) ||
      ancestors.some((ancestor) =>
        Object.entries(ancestor.extraStyles ?? {}).some(
          ([key, value]) =>
            [
              "overflow",
              "overflow-x",
              "overflow-y",
              "overflow-block",
              "overflow-inline",
            ].includes(key) && value !== "visible",
        ),
      )
    )
      return undefined;
    const transform = textLayerTransform(layer, parentTransform);
    transform[5] -= doc.height / 2;
    const leading =
      leadingFromLineHeight(layer.lineHeight, layer.fontSize) ??
      layer.fontSize * (measureFont(layer).ascent + measureFont(layer).descent);
    // Height slack keeps InDesign's font metrics from dropping the last line as overset text.
    const height = layer.height + leading * 2;
    const baseline =
      measurements.get(layer.id)?.baseline ??
      firstBaselineOffset(layer, measureFont(layer));
    const runs = segments.map((segment) => {
      const resolved = resolveFont?.({
        fontFamily: layer.fontFamily,
        fontWeight: segment.fontWeight,
        italic: segment.italic,
      });
      const family =
        resolved?.family ??
        parseFontFamilyName(layer.fontFamily, layer.fontFamily);
      const bold =
        Number.parseInt(segment.fontWeight, 10) >= 600 ||
        segment.fontWeight === "bold" ||
        segment.fontWeight === "bolder";
      // InDesign cannot reproduce the browser's simulated bold or italic face.
      if (
        resolved &&
        ((bold && !resolved.isBoldFace) ||
          (segment.italic && !resolved.isItalicFace))
      )
        return undefined;
      const fontStyle =
        resolved?.style ??
        (bold
          ? segment.italic
            ? "Bold Italic"
            : "Bold"
          : segment.italic
            ? "Italic"
            : "Regular");
      const size = segment.fontSize ?? layer.fontSize;
      return {
        ...segment,
        family,
        fontStyle,
        postScriptName: resolved?.name,
        size,
        tracking: trackingFromLetterSpacing(layer.letterSpacing, size) ?? 0,
      };
    });
    if (!runs.every((run) => run !== undefined)) return undefined;
    return { layer, transform, leading, height, baseline, runs };
  };
}

export type PreparedIdmlText = NonNullable<
  ReturnType<ReturnType<typeof createIdmlTextPreparer>>
>;

function inheritedWhitespace({ layer, ancestors }: EditableText) {
  let collapse = "collapse";
  let wrap = "wrap";
  for (const node of [...ancestors, layer]) {
    const parent = { collapse, wrap };
    for (const [key, value] of Object.entries(node.extraStyles ?? {})) {
      const inherited = value === "inherit" || value === "unset";
      if (key === "white-space") {
        if (inherited) ({ collapse, wrap } = parent);
        else if (value === "normal" || value === "initial") {
          collapse = "collapse";
          wrap = "wrap";
        } else {
          collapse =
            value === "nowrap"
              ? "collapse"
              : value === "pre-line"
                ? "preserve-breaks"
                : value;
          wrap = value === "nowrap" || value === "pre" ? "nowrap" : "wrap";
        }
      } else if (key === "white-space-collapse") {
        collapse = inherited
          ? parent.collapse
          : value === "initial"
            ? "collapse"
            : value;
      } else if (key === "text-wrap-mode" || key === "text-wrap") {
        wrap = inherited ? parent.wrap : value === "initial" ? "wrap" : value;
      }
    }
  }
  return { collapse, wrap };
}

function idmlTextSegments(layer: TextLayer, collapse: string) {
  if (collapse !== "preserve-breaks") return textSegmentsOf(layer);
  const dom = new DOMParser().parseFromString(layer.content, "text/html");
  const walker = dom.createTreeWalker(dom.body, NodeFilter.SHOW_TEXT);
  const nodes: Node[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  for (const node of nodes) {
    const parts = (node.textContent ?? "").split(/\r?\n/);
    if (parts.length < 2) continue;
    const replacement = dom.createDocumentFragment();
    parts.forEach((part, index) => {
      if (index > 0) replacement.appendChild(dom.createElement("br"));
      replacement.appendChild(dom.createTextNode(part));
    });
    node.parentNode?.replaceChild(replacement, node);
  }
  return textSegmentsOf({ ...layer, content: dom.body.innerHTML });
}

function supportsNativeText(
  layer: TextLayer,
  segments: ReturnType<typeof textSegmentsOf>,
): boolean {
  if (
    layer.width <= 0 ||
    layer.height <= 0 ||
    layer.maxLines ||
    layer.textShadow.length ||
    layer.shadow.length ||
    layer.border ||
    layer.fill
  )
    return false;
  if (segments.some((segment) => parseCssColor(segment.color).alpha !== 1))
    return false;
  // CSS decoration and layout without an IDML counterpart stays in the browser-rendered bitmap.
  const supported = new Set([
    "text-transform",
    "mix-blend-mode",
    "transform-origin",
    "white-space",
    "white-space-collapse",
    "text-wrap-mode",
    "text-wrap",
    "box-sizing",
    "word-break",
    "font-feature-settings",
    "font-kerning",
    "text-rendering",
    "overflow",
  ]);
  if (Object.keys(layer.extraStyles ?? {}).some((key) => !supported.has(key)))
    return false;
  if (layer.extraStyles?.overflow && layer.extraStyles.overflow !== "visible")
    return false;
  const casing = layer.extraStyles?.["text-transform"];
  return !casing || casing === "none" || casing === "uppercase";
}
